import { useEffect, useState, type FormEvent } from 'react';
import {
  createPost,
  deletePost,
  listPosts,
  updatePost,
  type Post,
} from '../api/posts';
import { listTags } from '../api/tags';
import { listStages, type Stage } from '../api/stages';
import { HttpError } from '../api/client';
import { TagsInput } from './TagsInput';
import { PhotoSelector } from './PhotoSelector';

type Mode = { kind: 'idle' } | { kind: 'creating' } | { kind: 'editing'; post: Post };

export function PostsSection({ tripId }: { tripId: string }) {
  const [posts, setPosts] = useState<Post[] | null>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: 'idle' });
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      const [pp, ss, tt] = await Promise.all([
        listPosts(tripId),
        listStages(tripId),
        listTags().catch(() => [] as string[]),
      ]);
      setPosts(pp);
      setStages(ss);
      setAllTags(tt);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento diario');
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  async function onDelete(post: Post) {
    if (!confirm('Eliminare questo post? Le foto resteranno nel viaggio.')) return;
    setBusy(true);
    try {
      await deletePost(tripId, post.id);
      await refresh();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Errore eliminazione');
    } finally {
      setBusy(false);
    }
  }

  // Raggruppa per data
  const grouped: { date: string; items: Post[] }[] = (() => {
    if (!posts) return [];
    const map = new Map<string, Post[]>();
    for (const p of posts) {
      const arr = map.get(p.date) ?? [];
      arr.push(p);
      map.set(p.date, arr);
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, items]) => ({ date, items }));
  })();

  return (
    <section className="posts-section">
      <div className="section-head">
        <h2>Diario</h2>
        {mode.kind === 'idle' && (
          <button
            className="btn btn-primary"
            onClick={() => setMode({ kind: 'creating' })}
            disabled={busy}
          >
            + Nuovo post
          </button>
        )}
      </div>

      {error && <p className="err">{error}</p>}

      {mode.kind === 'creating' && (
        <PostForm
          tripId={tripId}
          stages={stages}
          allTags={allTags}
          onCancel={() => setMode({ kind: 'idle' })}
          onSubmit={async (data) => {
            await createPost(tripId, data);
            setMode({ kind: 'idle' });
            await refresh();
          }}
        />
      )}

      {mode.kind === 'editing' && (
        <PostForm
          tripId={tripId}
          stages={stages}
          allTags={allTags}
          initial={mode.post}
          onCancel={() => setMode({ kind: 'idle' })}
          onSubmit={async (data) => {
            await updatePost(tripId, mode.post.id, data);
            setMode({ kind: 'idle' });
            await refresh();
          }}
        />
      )}

      {!posts && !error && <p className="muted">Caricamento…</p>}

      {posts && posts.length === 0 && mode.kind === 'idle' && (
        <p className="muted">Nessun post nel diario. Inizia a raccontare il tuo viaggio.</p>
      )}

      {grouped.map((g) => (
        <div key={g.date} className="day-group">
          <h3 className="day-head">{formatDayHeader(g.date)}</h3>
          <ul className="post-list">
            {g.items.map((p) => {
              const stageTitle = stages.find((s) => s.id === p.stageId)?.title;
              return (
                <li key={p.id} className="post-card">
                  <header className="post-meta">
                    {stageTitle && <span className="stage-pill">📍 {stageTitle}</span>}
                    {p.tags.map((t) => (
                      <span key={t} className="tag-chip readonly">
                        {t}
                      </span>
                    ))}
                  </header>
                  <div className="post-body">{p.body}</div>
                  {p.photos.length > 0 && (
                    <ul className="post-photos">
                      {p.photos.map((ph) => (
                        <li key={ph.id}>
                          <img src={ph.url} alt={ph.caption ?? ''} loading="lazy" />
                        </li>
                      ))}
                    </ul>
                  )}
                  <footer className="post-actions">
                    <button
                      className="btn btn-ghost"
                      disabled={busy}
                      onClick={() => setMode({ kind: 'editing', post: p })}
                    >
                      Modifica
                    </button>
                    <button
                      className="btn btn-danger"
                      disabled={busy}
                      onClick={() => void onDelete(p)}
                    >
                      Elimina
                    </button>
                  </footer>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}

type FormData = {
  date: string;
  body: string;
  stageId: string | null;
  tags: string[];
  photoIds: string[];
};

function PostForm({
  tripId,
  stages,
  allTags,
  initial,
  onCancel,
  onSubmit,
}: {
  tripId: string;
  stages: Stage[];
  allTags: string[];
  initial?: Post;
  onCancel: () => void;
  onSubmit: (data: FormData) => Promise<void>;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(initial?.date ?? today);
  const [body, setBody] = useState(initial?.body ?? '');
  const [stageId, setStageId] = useState<string>(initial?.stageId ?? '');
  const [tags, setTags] = useState<string[]>(initial?.tags ?? []);
  const [photoIds, setPhotoIds] = useState<string[]>(
    initial?.photos.map((p) => p.id) ?? [],
  );
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!body.trim()) {
      setError('Il post non può essere vuoto');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        date,
        body: body.trim(),
        stageId: stageId || null,
        tags,
        photoIds,
      });
    } catch (err) {
      setError(err instanceof HttpError ? err.apiError.message : 'Errore di connessione');
      setSubmitting(false);
    }
  }

  return (
    <>
      <form className="form-card post-form" onSubmit={handleSubmit}>
        <label>
          <span>Testo *</span>
          <textarea
            rows={6}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
            maxLength={20000}
            placeholder="Cos'è successo oggi…"
            autoFocus
          />
        </label>

        <div className="row">
          <label>
            <span>Data *</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </label>
          <label>
            <span>Tappa (opzionale)</span>
            <select value={stageId} onChange={(e) => setStageId(e.target.value)}>
              <option value="">— nessuna —</option>
              {stages.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label>
          <span>Tag</span>
          <TagsInput value={tags} onChange={setTags} suggestions={allTags} />
        </label>

        <div className="photo-attach">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setPhotoModalOpen(true)}
          >
            📷 Allega foto ({photoIds.length})
          </button>
          {photoIds.length > 0 && (
            <span className="muted small">
              Le foto si selezionano dalla galleria del viaggio (caricale prima nella sezione
              Foto).
            </span>
          )}
        </div>

        {error && <p className="err">{error}</p>}

        <div className="actions">
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={submitting}>
            Annulla
          </button>
          <button type="submit" disabled={submitting || !body.trim()}>
            {submitting ? 'Salvataggio…' : initial ? 'Salva modifiche' : 'Pubblica post'}
          </button>
        </div>
      </form>

      {photoModalOpen && (
        <PhotoSelector
          tripId={tripId}
          initialSelected={photoIds}
          onCancel={() => setPhotoModalOpen(false)}
          onConfirm={(ids) => {
            setPhotoIds(ids);
            setPhotoModalOpen(false);
          }}
        />
      )}
    </>
  );
}

function formatDayHeader(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}
